import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { estimateFromEntry, formatMoney, formatThaiDate } from '../../utils/coopEstimate';
import './AdminUsageReport.css';
import './AdminShareLogs.css';
import './AdminCoopSavings.css';

// created_at เก็บเป็นเวลาไทย wall-clock แล้ว (เหมือน share logs) ไม่ต้องแปลง timezone
function thaiDateTime(s) {
    if (!s) return '-';
    const str = String(s).replace('T', ' ').replace('Z', '').split('.')[0];
    const [d, t] = str.split(' ');
    return `${formatThaiDate(d)} ${t ? t.slice(0, 5) : ''}`;
}

function AdminCoopSavings() {
    const [stats, setStats] = useState(null);
    const [entries, setEntries] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState({ search: '', latestOnly: true });
    const [page, setPage] = useState(1);

    const fetchStats = useCallback(async () => {
        try {
            const res = await api.get('/coop/admin/stats');
            setStats(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'โหลดสถิติไม่สำเร็จ');
        }
    }, []);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/coop/admin/entries', {
                params: { search: applied.search, latest_only: applied.latestOnly ? '1' : '0', page, limit: 50 }
            });
            setEntries(res.data.entries || []);
            setPagination(res.data.pagination || { page: 1, totalPages: 1, total: 0 });
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'โหลดรายการไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    }, [applied, page]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const submitFilters = (e) => {
        e.preventDefault();
        setPage(1);
        setApplied(a => ({ ...a, search }));
    };

    const toggleLatest = (v) => {
        setPage(1);
        setApplied(a => ({ ...a, latestOnly: v }));
    };

    const s = stats || {};

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>ยอดสหกรณ์ออมทรัพย์ที่สมาชิกบันทึก</h1>
                <p>ข้อมูลที่ผู้ใช้กรอกเองจากหนังสือยืนยันยอดของสหกรณ์ (ไม่ได้เชื่อมกับสหกรณ์) — เห็นได้เฉพาะผู้ดูแลระบบ (Admin)</p>
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

            <div className="usage-cards">
                <div className="usage-card users">
                    <span className="usage-card-value">{formatMoney(s.total_users).replace('.00', '')}</span>
                    <span className="usage-card-label">สมาชิกที่บันทึกยอด</span>
                    <span className="usage-card-sub">{formatMoney(s.total_entries).replace('.00', '')} รายการรวม</span>
                </div>
                <div className="usage-card slip">
                    <span className="usage-card-value">{formatMoney(s.sum_balance)}</span>
                    <span className="usage-card-label">หุ้นสหกรณ์รวม (บาท)</span>
                    <span className="usage-card-sub">ตามหนังสือล่าสุดของแต่ละคน</span>
                </div>
                <div className="usage-card tawi">
                    <span className="usage-card-value">{formatMoney(s.sum_special)}</span>
                    <span className="usage-card-label">เงินฝากออมทรัพย์พิเศษรวม (บาท)</span>
                    <span className="usage-card-sub">{formatMoney(s.users_with_special).replace('.00', '')} บัญชีที่บันทึก</span>
                </div>
                <div className="usage-card request">
                    <span className="usage-card-value">{formatMoney(s.sum_monthly)}</span>
                    <span className="usage-card-label">หักซื้อหุ้นรวมต่อเดือน (บาท)</span>
                    <span className="usage-card-sub">{formatMoney(s.users_with_monthly).replace('.00', '')} คนที่ระบุ</span>
                </div>
                <div className="usage-card share">
                    <span className="usage-card-value">{formatMoney(s.sum_loan)}</span>
                    <span className="usage-card-label">ยอดกู้คงเหลือรวม (บาท)</span>
                    <span className="usage-card-sub">
                        {formatMoney(s.users_with_loan).replace('.00', '')} คน · หักชำระรวม {formatMoney(s.sum_loan_monthly)}/เดือน
                    </span>
                </div>
                <div className="usage-card email">
                    <span className="usage-card-value">{formatMoney(s.popup_dismissed).replace('.00', '')}</span>
                    <span className="usage-card-label">กด "ไม่ต้องแสดงอีก"</span>
                    <span className="usage-card-sub">
                        ปิด popup เชิญถาวร · ปิดข่าวยอดกู้ {formatMoney(s.loan_announce_dismissed).replace('.00', '')} คน
                    </span>
                </div>
            </div>

            <form className="share-filters" onSubmit={submitFilters}>
                <label>ค้นหา <input type="text" placeholder="ชื่อ / CID / รหัสพนักงาน" value={search} onChange={e => setSearch(e.target.value)} /></label>
                <label className="coop-admin-check">
                    <input type="checkbox" checked={applied.latestOnly} onChange={e => toggleLatest(e.target.checked)} />
                    เฉพาะรายการล่าสุดของแต่ละคน
                </label>
                <button type="submit" className="steam-btn steam-btn-primary">ค้นหา</button>
            </form>

            <div className="steam-card">
                <div className="steam-card-header">
                    <span>{applied.latestOnly ? 'ยอดล่าสุดรายคน' : 'รายการทั้งหมด (รวมประวัติ)'}</span>
                    <span className="badge badge-primary">{formatMoney(pagination.total).replace('.00', '')} รายการ</span>
                </div>
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: 40 }}><div className="spinner"></div></div>
                    ) : (
                        <table className="usage-table coop-admin-table">
                            <thead>
                                <tr>
                                    <th>สมาชิก</th>
                                    <th>ประเภท</th>
                                    <th>ณ วันที่</th>
                                    <th className="num">ยอดตามหนังสือ</th>
                                    <th className="num">หัก/เดือน</th>
                                    <th className="num">ประมาณการวันนี้</th>
                                    <th>หมายเหตุ</th>
                                    <th>บันทึกเมื่อ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.length === 0 ? (
                                    <tr><td colSpan="8" className="share-empty">ไม่พบรายการ</td></tr>
                                ) : entries.map(e => {
                                    const est = estimateFromEntry(e);
                                    const isLatest = e.rn === 1;
                                    const isSpecial = e.account_type === 'special';
                                    const isLoan = e.account_type === 'loan';
                                    const estTitle = est.hasMonthly
                                        ? `${isLoan ? '−' : '+'}${formatMoney(est.monthly)} × ${est.months} เดือน`
                                        : (isSpecial ? 'เงินฝากพิเศษ — แสดงยอดตามหนังสือ (ไม่ทบยอด)' : 'ไม่ได้ระบุยอดหักรายเดือน');
                                    return (
                                        <tr key={e.id} className={isLatest ? '' : 'coop-admin-old'}>
                                            <td>
                                                <div className="coop-admin-user">
                                                    {e.avatar_url
                                                        ? <img src={`/api${e.avatar_url}`} alt="" className="coop-admin-avatar" />
                                                        : <span className="coop-admin-avatar coop-admin-avatar-ph">{(e.fullname || '?').charAt(0)}</span>}
                                                    <div>
                                                        <div>{e.fullname || '-'}</div>
                                                        <div className="share-muted">{e.emp_code ? `${e.emp_code} · ` : ''}{e.cid}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="nowrap">
                                                <span className={`coop-type-chip ${isLoan ? 'coop-type-loan' : isSpecial ? 'coop-type-special' : 'coop-type-share'}`}>
                                                    {isLoan ? 'กู้' : isSpecial ? 'ฝากพิเศษ' : 'หุ้น'}
                                                </span>
                                            </td>
                                            <td className="nowrap">
                                                {formatThaiDate(e.confirmed_date)}
                                                {!isLatest && <span className="share-muted"> (เก่า)</span>}
                                            </td>
                                            <td className="num nowrap">{formatMoney(e.balance)}</td>
                                            <td className="num nowrap">{e.monthly_share != null ? formatMoney(e.monthly_share) : <span className="share-muted">-</span>}</td>
                                            <td className={`num nowrap coop-admin-est ${isLoan ? 'coop-admin-est-loan' : ''}`} title={estTitle}>
                                                {formatMoney(est.estimated)}
                                                {est.hasMonthly && est.months > 0 && <span className="share-muted"> ({isLoan ? '−' : '+'}{est.months} ด.)</span>}
                                            </td>
                                            <td className="coop-admin-note">{e.note || <span className="share-muted">-</span>}</td>
                                            <td className="share-muted nowrap">{thaiDateTime(e.created_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {pagination.totalPages > 1 && (
                    <div className="share-pagination">
                        <button className="range-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ ก่อนหน้า</button>
                        <span>หน้า {pagination.page} / {pagination.totalPages}</span>
                        <button className="range-btn" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>ถัดไป ›</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminCoopSavings;
